import React, { useEffect } from "react";
import { ScrollNavbar } from "../../components";
import Footer from "../../components/Footer/Footer";
import Banner from "../../components/Landing/Banner";
import AboutUsImg from "../../assets/images/aboutus.png";
import "aos/dist/aos.css";
import AOS from "aos";

const About = () => {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  const coreValues = [
    {
      title: "Customer-Centricity",
      description:
        "We put our customers at the heart of everything we do, providing seamless, reliable, and enjoyable experiences.",
      icon: "👥",
    },
    {
      title: "Innovation",
      description:
        "We embrace new technologies and creative solutions to streamline processes and deliver exceptional value.",
      icon: "💡",
    },
    {
      title: "Transparency",
      description:
        "Honesty and clear communication are our foundations, ensuring customers always know what to expect.",
      icon: "🔍",
    },
    {
      title: "Reliability",
      description:
        "We strive to be a trusted partner with consistently reliable and punctual services for peace of mind.",
      icon: "🛡️",
    },
    {
      title: "Sustainability",
      description:
        "We're dedicated to eco-friendly technologies and practices that benefit our planet and communities.",
      icon: "🌱",
    },
    {
      title: "Empowerment",
      description:
        "We foster growth, learning, and inclusivity for our team members, partners, and communities we serve.",
      icon: "🚀",
    },
    {
      title: "Social Responsibility",
      description:
        "We're committed to making a positive impact through socially conscious initiatives and ethical practices.",
      icon: "🤝",
    },
  ];

  return (
    <>
      <ScrollNavbar />
      <div
        className="px-4 sm:px-8 lg:px-12"
        style={{ maxWidth: "1400px", margin: "0 auto" }}
      >
        {/* Hero Section */}
        <section className="my-20">
          <div className="w-full h-full box-border p-8 flex justify-center items-center md:flex-row flex-col-reverse relative rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-blue-50/20 rounded-3xl z-0"></div>

            <div
              className="md:w-2/5 w-full flex justify-center items-center h-full z-10 relative"
              data-aos="fade-right"
            >
              <div className="h-5/6 relative">
                <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-blue-100/50 filter blur-xl"></div>
                <img
                  alt="About Fusion Cab"
                  src={AboutUsImg}
                  className="w-[90%] drop-shadow-2xl rounded-lg relative"
                  style={{ maxHeight: "550px" }}
                />
                <div className="absolute -bottom-4 -right-4 w-32 h-32 rounded-full bg-blue-100/70 filter blur-xl"></div>
              </div>
            </div>

            <div className="md:w-3/5 w-full z-10 relative">
              <div className="w-full flex flex-col gap-4">
                <div
                  className="font-mazzardbold text-4xl md:text-5xl my-3 bg-clip-text text-transparent bg-gradient-to-r from-black to-blue-900"
                  data-aos="fade-left"
                >
                  About Us
                </div>
                <div className="my-2 text-left flex flex-col gap-4">
                  <p
                    data-aos="fade-left"
                    data-aos-delay="400"
                    className="text-gray-800 leading-relaxed"
                  >
                    At{" "}
                    <strong className="font-mazzardbold text-blue-900">
                      Fusion Cab
                    </strong>
                    , we are more than just a transportation service; we are a
                    revolutionary force in the cab rental industry. Our mission
                    is to bridge the gap between technology and transportation,
                    offering a seamless, efficient, and customer-centric
                    experience.
                  </p>
                  <p
                    data-aos="fade-left"
                    data-aos-delay="600"
                    className="text-gray-800 leading-relaxed"
                  >
                    Our cutting-edge tech platform connects businesses,
                    intermediaries, and service providers in a way that ensures
                    a smooth journey from booking to destination.
                  </p>
                  <p
                    data-aos="fade-left"
                    data-aos-delay="800"
                    className="text-gray-800 leading-relaxed"
                  >
                    We believe in the power of innovation and are committed to
                    transforming the commercial cab rental landscape into one
                    that is organized, reliable, and accessible to all.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="my-20">
          <div className="w-full h-full flex box-border p-8 py-16 bg-gray-50 rounded-3xl">
            <div className="w-full h-full flex flex-col items-center justify-center">
              <div className="w-full text-center mb-12">
                <h2
                  className="text-3xl md:text-5xl font-mazzardbold bg-clip-text text-transparent bg-gradient-to-r from-black to-blue-900"
                  data-aos="fade-up"
                >
                  Our Story
                </h2>
                <p
                  className="text-gray-600 mt-4 max-w-2xl mx-auto"
                  data-aos="fade-up"
                  data-aos-delay="200"
                >
                  Born from a vision to bring order to the chaotic world of
                  commercial cab rentals
                </p>
              </div>
              <div className="max-w-4xl mx-auto text-gray-700 space-y-6">
                <p
                  className="leading-relaxed"
                  data-aos="fade-up"
                  data-aos-delay="300"
                >
                  Fusion Cab was born out of a desire to bring order to the
                  often chaotic world of commercial cab rentals. Recognizing the
                  challenges faced by travelers - unpredictable pricing,
                  unreliable service, and a lack of transparency - we set out to
                  create a solution that would change the game.
                </p>
                <p
                  className="leading-relaxed"
                  data-aos="fade-up"
                  data-aos-delay="400"
                >
                  Our journey began with a vision to revolutionize the industry
                  through technology and innovation. We understood that to truly
                  make a difference, we needed to focus not just on providing
                  rides but on creating an entire ecosystem where all
                  stakeholders could thrive.
                </p>
                <p
                  className="leading-relaxed"
                  data-aos="fade-up"
                  data-aos-delay="500"
                >
                  Today, Fusion Cab stands as a testament to what's possible
                  when passion meets purpose. We are proud to offer services
                  that are not only efficient and dependable but also socially
                  conscious.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values Section */}
        <section className="my-20">
          <div className="w-full text-center mb-12">
            <h2
              className="text-3xl md:text-5xl font-mazzardbold bg-clip-text text-transparent bg-gradient-to-r from-black to-blue-900"
              data-aos="fade-up"
            >
              Our Core Values
            </h2>
            <p
              className="text-gray-600 mt-4 max-w-2xl mx-auto"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              The principles that guide everything we do at Fusion Cab
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreValues.map((value, index) => (
              <div
                key={index}
                className="group"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 p-6 h-full">
                  <div className="text-4xl mb-4 text-center">{value.icon}</div>
                  <h3 className="text-xl font-mazzardbold text-gray-900 mb-4 text-center">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Banner Section */}
        <section className="my-20">
          <Banner />
        </section>
      </div>
      <Footer />
    </>
  );
};

export default About;
